import { graphqlRequest } from "../graphqlClient";
import type {
  ScenarioTransactionViewConnection,
  ScenarioTransactionViewFilterInput,
} from "../models/scenarioTransactionView";

export interface ScenarioTransactionViewSortInput {
  field: string;
  order: "Ascending" | "Descending";
}

/** Ensures every request is scoped to a single scenario. */
function withScenario(
  scenarioId: number,
  filter?: ScenarioTransactionViewFilterInput,
): ScenarioTransactionViewFilterInput {
  return { ...filter, scenarioId: { eq: scenarioId } };
}

interface ScenarioTransactionViewListResponse {
  scenarios: {
    scenarioTransactionViewList: ScenarioTransactionViewConnection;
  };
}

export async function getScenarioTransactionsGraphql(
  scenarioId: number,
  filter?: ScenarioTransactionViewFilterInput,
  first?: number,
  after?: string,
  sort?: ScenarioTransactionViewSortInput[],
): Promise<ScenarioTransactionViewConnection> {
  const query = `
    query ScenarioTransactionViewList($filter: ScenarioTransactionViewFilterInput, $first: Int, $after: String, $sort: [ScenarioTransactionViewSortSortByInput!]) {
      scenarios {
        scenarioTransactionViewList(filter: $filter, first: $first, after: $after, sort: $sort) {
          edges {
            cursor
            node {
              id
              scenarioId
              forecastDate
              dueDate
              counterpartyName
              description
              category
              direction
              type
              grossAmount
              outstandingAmount
              layer
              status
              reference
              sourceId
              dataSourceName
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<ScenarioTransactionViewListResponse>(
    query,
    {
      filter: withScenario(scenarioId, filter),
      first,
      after,
      sort,
    },
  );
  return data.scenarios.scenarioTransactionViewList;
}

interface ScenarioTransactionViewCountResponse {
  scenarios: {
    scenarioTransactionViewCount: number;
  };
}

export async function getScenarioTransactionsCount(
  scenarioId: number,
  filter?: ScenarioTransactionViewFilterInput,
): Promise<number> {
  const query = `
    query ScenarioTransactionViewCount($filter: ScenarioTransactionViewFilterInput) {
      scenarios {
        scenarioTransactionViewCount(filter: $filter)
      }
    }
  `;
  const data = await graphqlRequest<ScenarioTransactionViewCountResponse>(
    query,
    {
      filter: withScenario(scenarioId, filter),
    },
  );
  return data.scenarios.scenarioTransactionViewCount;
}

interface ScenarioTransactionViewDistinctValuesResponse {
  scenarios: {
    scenarioTransactionViewDistinctValues: string[];
  };
}

export async function getScenarioTransactionDistinctValues(
  scenarioId: number,
  field: string,
): Promise<string[]> {
  const query = `
    query ScenarioTransactionViewDistinctValues($scenarioId: Int!, $field: String!) {
      scenarios {
        scenarioTransactionViewDistinctValues(scenarioId: $scenarioId, field: $field)
      }
    }
  `;
  const data =
    await graphqlRequest<ScenarioTransactionViewDistinctValuesResponse>(query, {
      scenarioId,
      field,
    });
  return data.scenarios.scenarioTransactionViewDistinctValues;
}
